import React, { useEffect, useState, useRef } from 'react';
import {
  Mic, Upload, BarChart3, FolderOpen, Search, ArrowRight,
  SkipForward, Play, Keyboard, Rocket, RotateCcw, HelpCircle,
  type LucideIcon,
} from 'lucide-react';
import type { DemoDecisionConfig } from '../../types/demo';

const iconMap: Record<string, LucideIcon> = {
  mic: Mic,
  upload: Upload,
  'bar-chart': BarChart3,
  folder: FolderOpen,
  search: Search,
  'arrow-right': ArrowRight,
  'skip-forward': SkipForward,
  play: Play,
  keyboard: Keyboard,
  rocket: Rocket,
  repeat: RotateCcw,
  'help-circle': HelpCircle,
};

interface DemoDecisionProps {
  decision: DemoDecisionConfig;
  onSelect: (nextStepId: string) => void;
}

/** Decision point UI with choice buttons and countdown timeout ring */
export const DemoDecision: React.FC<DemoDecisionProps> = ({ decision, onSelect }) => {
  const [timeRemaining, setTimeRemaining] = useState(decision.timeoutMs);
  const startTimeRef = useRef(Date.now());

  const hasTimeout = decision.timeoutMs > 0 && decision.defaultOptionIndex >= 0;

  useEffect(() => {
    if (!hasTimeout) return;
    startTimeRef.current = Date.now();
    setTimeRemaining(decision.timeoutMs);

    const interval = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      const remaining = Math.max(0, decision.timeoutMs - elapsed);
      setTimeRemaining(remaining);

      if (remaining <= 0) {
        clearInterval(interval);
        const defaultOption = decision.options[decision.defaultOptionIndex];
        if (defaultOption) onSelect(defaultOption.nextStepId);
      }
    }, 50);

    return () => clearInterval(interval);
  }, [decision, onSelect, hasTimeout]);

  const progress = hasTimeout ? timeRemaining / decision.timeoutMs : 1;
  const circumference = 2 * Math.PI * 14; // r=14
  const dashOffset = circumference * (1 - progress);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 mb-1">
        <p className="text-sm text-white/90 font-medium">{decision.prompt}</p>
        {/* Countdown ring */}
        <svg width="32" height="32" viewBox="0 0 32 32" className="shrink-0">
          <circle cx="16" cy="16" r="14" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="2" />
          <circle
            cx="16"
            cy="16"
            r="14"
            fill="none"
            stroke="#10b981"
            strokeWidth="2"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            transform="rotate(-90 16 16)"
            className="transition-all duration-100"
          />
          <text x="16" y="16" textAnchor="middle" dominantBaseline="central" fill="white" fontSize="10">
            {Math.ceil(timeRemaining / 1000)}
          </text>
        </svg>
      </div>
      <div className="flex flex-col gap-2 sm:gap-1.5">
        {decision.options.map((option, i) => {
          const Icon = option.icon ? iconMap[option.icon] : null;
          return (
            <button
              key={i}
              onClick={() => onSelect(option.nextStepId)}
              className="flex items-center gap-2 px-4 sm:px-3 py-3 sm:py-1.5 rounded-full text-sm sm:text-xs font-medium
                bg-white/10 hover:bg-white/20 text-white border border-white/20
                transition-colors duration-150 text-left"
            >
              {Icon && <Icon className="h-4 w-4 sm:h-3.5 sm:w-3.5 text-white/70 shrink-0" />}
              <span>{option.label}</span>
              {i === decision.defaultOptionIndex && (
                <span className="ml-auto text-emerald-300 text-[10px]">default</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};
